// The `AGClub` component dynamically renders different sections based on the current URL path.
// It handles multiple templates like club home, FAQs, terms and conditions, and discounts
// by selecting the appropriate content based on the `visualType`. The component includes a
// breadcrumb navigation, a mobile-friendly dropdown for club selection, and a desktop accordion
// for the same purpose. It uses `useEffect` to update the displayed template and breadcrumb based
// on the current route. Additionally, it integrates an `AGStoreLocator` and renders the selected
// template in the main content area. The routing is handled using Next.js's `useRouter`.

import React, { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import {
  AGClubContent,
  AmericanGolfClub,
  BirthdayDiscount,
  ClubHomeType,
  FAQ,
  Templates,
  TermsAndConditions,
} from 'types/next';
import AccordionBtn from 'components/commercetools-ui/atoms/accordion';
import AGStoreLocator from './storeLocator';
import ClubHome from './clubhome';
import FAQS from './faqs';
import TermsAndCondition from './termsAndCondition';
import Discounts from './discounts';
import { useRouter } from 'next/navigation';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';

interface AGClub {
  agContent: AGClubContent;
}

interface BreadcrumbItem {
  name: string;
  link: string;
}

type Template = ClubHomeType | FAQ | TermsAndConditions | BirthdayDiscount | null;

const defaultBreadcrumb = [
  { name: 'Home', link: '/' },
  { name: 'American Golf Club', link: '/club-page' },
];

const AGClub = ({ agContent }: AGClub) => {
  const [currentTemplate, setCurrentTemplate] = useState<Template>(null);
  const [currentVisualType, setCurrentVisualType] = useState<string>('');
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedClubName, setSelectedClubName] = useState('American Golf Club');
  const pathname = usePathname();
  const router = useRouter();

  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);

  useEffect(() => {
    if (pathname === '/club-page/') {
      const clubHomeData: ClubHomeType | null = agContent?.templates?.clubhome?.[0] || null;
      setCurrentTemplate(clubHomeData);
      setCurrentVisualType('clubhome');
      setBreadcrumb(defaultBreadcrumb);
      return;
    }

    const clubData: AmericanGolfClub | undefined = agContent?.americanGolfClub?.find((club: AmericanGolfClub) =>
      pathname?.includes(club.link),
    );

    if (clubData) {
      const { name, link, templateType, visualType } = clubData;

      const visualTemplates = agContent?.templates?.[visualType as keyof Templates];
      if (visualTemplates && Array.isArray(visualTemplates)) {
        const templateData = visualTemplates?.find(
          (template) =>
            'templateType' in template && template.templateType.toLowerCase() === templateType.toLowerCase(),
        ) as Template;

        setCurrentTemplate(templateData || null);
        setCurrentVisualType(visualType);

        const newBreadcrumb = [...defaultBreadcrumb, { name, link }];
        setBreadcrumb(newBreadcrumb);
        setSelectedClubName(name);
      } else {
        setCurrentTemplate(null);
        setCurrentVisualType('');
        console.error(`Visual type "${visualType}" not found in templates.`);
      }
    } else {
      setCurrentTemplate(null);
      setCurrentVisualType('');
      console.error(`No matching club data found for pathname: ${pathname}`);
    }
  }, [pathname, agContent]);

  const handleBreadcrumbClick = (link: string) => {
    router.push(link);
  };

  const renderVisualComponent = () => {
    switch (currentVisualType) {
      case 'clubhome':
        return <ClubHome agContent={currentTemplate as ClubHomeType} />;
      case 'faqs':
        return <FAQS agContent={currentTemplate as FAQ} />;
      case 'termsandconditons':
        return <TermsAndCondition agContent={currentTemplate as TermsAndConditions} />;
      case 'discounts':
        return <Discounts agContent={currentTemplate as BirthdayDiscount} />;
      default:
        if (pathname === '/club-page/') {
          const fallbackData = agContent?.templates?.clubhome?.[0] || null;
          return <ClubHome agContent={fallbackData} />;
        }
        return <div>No matching component found</div>;
    }
  };

  return (
    <>
      {/* Breadcrumb Section */}
      <div className="px-15 pb-50 pt-30 lg:mx-[90px] lg:px-30">
        <div className="flex items-center space-x-12 pb-30 text-sm">
          {breadcrumb?.map((crumb: BreadcrumbItem, index: number) => (
            <React.Fragment key={index}>
              <span
                onClick={() => handleBreadcrumbClick(crumb.link)}
                className={`my-4 ml-10 cursor-pointer hover:text-[#127e57] ${
                  index < breadcrumb.length - 1 ? 'font-normal' : ''
                } ${pathname === crumb.name ? 'font-semibold' : ''}`}
                title={crumb?.link}
              >
                <a href={crumb?.link} className="group relative ml-10 text-14">
                  {crumb?.name}
                </a>
              </span>
              {index < breadcrumb.length - 1 && <span className="mx-12 text-[#127e57]">{'>'}</span>}
            </React.Fragment>
          ))}
        </div>

        <div className="gap-x-5 md:flex md:flex-row xl:gap-x-10">
          <div className="w-full pl-15 pr-30 text-sm font-normal md:w-[30%] md:min-w-[285px]">
            <div className="mb-4 block md:hidden">
              <button
                onClick={toggleDropdown}
                className="relative h-40 w-full rounded border px-14 text-left font-semibold"
              >
                {selectedClubName}
                <span className="absolute right-16 top-1/2 -translate-y-1/2 transform">
                  {isDropdownOpen ? (
                    <ChevronUpIcon className="h-15 w-15 text-[#127e57]" />
                  ) : (
                    <ChevronDownIcon className="h-15 w-15 text-[#127e57]" />
                  )}
                </span>
              </button>
              {isDropdownOpen && (
                <ul className="border-x border-b">
                  {agContent?.americanGolfClub?.map((club: AmericanGolfClub, index: number) => {
                    const isActive = pathname === club.link;
                    return (
                      <li
                        key={index}
                        className={`my-3 ml-10 flex cursor-pointer items-center px-3 py-4 hover:text-[#127e57] ${
                          isActive ? 'text-[#127e57]' : ''
                        }`}
                        onClick={() => {
                          router.push(club?.link);
                          setSelectedClubName(club?.name);
                        }}
                      >
                        {club?.name}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Desktop Accordion */}
            <div className="accordian-section mb-40 hidden md:block">
              <AccordionBtn
                variant="plusAndMinus"
                className="col-span-2 border-b border-neutral-400 md:mt-0"
                closedSectionTitle="American Golf Club"
                buttonClassName="py-15 font-semibold text-16 leading-tight text-primary-black pl-0 md:pr-12 border-t border-b"
                panelClassName="pb-20 md:pt-10 md:pb-30 text-primary-black"
                defaultOpen={agContent?.americanGolfClub?.some((club) => pathname === club.link)}
              >
                <ul>
                  {agContent?.americanGolfClub?.map((club: AmericanGolfClub, index: number) => {
                    const isActive = pathname === club.link;
                    return (
                      <li
                        className={`flex cursor-pointer items-center hover:text-[#127e57] ${
                          isActive ? 'text-[#127e57]' : ''
                        }`}
                        key={index}
                        onClick={() => router.push(club?.link)}
                      >
                        <span className="mr-3 pt-1 text-16 font-semibold">{'>'}</span>
                        {club?.name}
                      </li>
                    );
                  })}
                </ul>
              </AccordionBtn>
            </div>

            <AGStoreLocator />
          </div>
          <main role="main" className="w-full p-15 pt-1">
            {renderVisualComponent()}
          </main>
        </div>
      </div>
    </>
  );
};

export default AGClub;
